import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User } from '../api/types';

interface AuthSliceState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  needsOTP: boolean;
  otpEmail: string | null;
}

const initialState: AuthSliceState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,  // Start with loading to check auth
  error: null,
  needsOTP: false,
  otpEmail: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    loginSuccess: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.isLoading = false;
      state.error = null;
      state.needsOTP = false;
      state.otpEmail = null;
    },

    loginOTPRequired: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.needsOTP = true;
      state.otpEmail = action.payload;
    },

    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.needsOTP = false;
      state.otpEmail = null;
    },

    setOTPState: (state, action: PayloadAction<{ needsOTP: boolean; otpEmail: string | null }>) => {
      state.needsOTP = action.payload.needsOTP;
      state.otpEmail = action.payload.otpEmail;
    },

    clearOTPState: (state) => {
      state.needsOTP = false;
      state.otpEmail = null;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  clearError,
  loginSuccess,
  loginOTPRequired,
  logout,
  setOTPState,
  clearOTPState,
} = authSlice.actions;

export default authSlice.reducer;